import { useLang } from "@/context/LanguageContext";
import { useDemoForm } from "@/context/DemoFormContext";
import { Check, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export const Pricing = () => {
  const { t } = useLang();
  const { openForm } = useDemoForm();

  const plans = [
    { key: "essence", ...t.pricing.plans.essence },
    { key: "atelier", ...t.pricing.plans.atelier, popular: true },
    { key: "maison", ...t.pricing.plans.maison },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32 bg-[#FAFAFA] overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full blur-3xl opacity-40 animate-blob"
        style={{ background: "radial-gradient(circle, #C4B5FD 0%, transparent 70%)" }} />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <Reveal>
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-5" data-testid="pricing-eyebrow">
              {t.pricing.eyebrow}
            </div>
            <h2
              data-testid="pricing-title"
              className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1A1A1A]"
            >
              {t.pricing.title} <span className="italic text-[#737373]">{t.pricing.title_accent}</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#4A4A4A] leading-relaxed font-light" data-testid="pricing-subtitle">
              {t.pricing.subtitle}
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p, i) => (
            <Reveal key={p.key} delay={i * 120}>
              <div
                data-testid={`pricing-card-${p.key}`}
                className={`relative h-full rounded-3xl border p-8 md:p-10 flex flex-col transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-20px_rgba(167,139,250,0.45)] ${
                  p.popular
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.3)] md:scale-[1.02]"
                    : "bg-white border-black/5"
                }`}
              >
                {p.popular && (
                  <div
                    data-testid="popular-badge"
                    className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-gradient-to-r from-[#A78BFA] to-[#67E8F9] text-white text-[10px] tracking-[0.25em] uppercase px-4 py-1.5 rounded-full"
                  >
                    <Sparkles size={12} /> {t.pricing.most_popular}
                  </div>
                )}
                <div className={`text-xs tracking-[0.3em] uppercase ${p.popular ? "text-white/60" : "text-[#737373]"}`}>
                  {p.name}
                </div>
                <h3 className={`mt-5 font-serif font-light text-3xl md:text-4xl tracking-tight italic ${p.popular ? "text-white" : "text-[#1A1A1A]"}`}>
                  {t.pricing.custom_price}
                </h3>
                <p className={`mt-3 font-light ${p.popular ? "text-white/80" : "text-[#4A4A4A]"}`}>{p.desc}</p>
                <ul className="mt-8 space-y-3 flex-1">
                  {p.features.map((f, idx) => (
                    <li
                      key={idx}
                      data-testid={`pricing-${p.key}-feature-${idx}`}
                      className={`flex items-start gap-3 text-sm font-light ${p.popular ? "text-white/90" : "text-[#1A1A1A]"}`}
                    >
                      <Check size={16} className={`mt-0.5 flex-shrink-0 ${p.popular ? "text-[#67E8F9]" : "text-[#A78BFA]"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-testid={`pricing-${p.key}-cta`}
                  onClick={() => openForm(p.key)}
                  className={`mt-10 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm tracking-wide transition-all ${
                    p.popular
                      ? "bg-white text-[#1A1A1A] hover:bg-white/90"
                      : "bg-[#1A1A1A] text-white hover:bg-[#333]"
                  }`}
                >
                  {t.pricing.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
